import { trackFacebookEvent, trackTikTokEvent } from '@/lib/adsTracking';
import { stanAnalytics } from '@/lib/stanAnalytics';
import { GOOGLE_AD_CONV_ACTION } from '@/lib/constants';

interface UserMeta {
    user_id?: string | number;
    username?: string;
    email?: string;
    full_name?: string;
}

interface PlanInfo {
    price: number;
    currency: string;
    planName: string;
    isYearly: boolean;
}

/**
 * Track subscription creation attempt
 */
export function trackSubscriptionAttempted(meta: UserMeta): void {
    stanAnalytics('subscription-creation-attempted', { meta });
}

/**
 * Track terms agreement
 */
export function trackTermsAgreed(meta: UserMeta): void {
    stanAnalytics('subscription-creation-terms-agreed', { meta });
}

/**
 * Track payment method creation
 */
export function trackPaymentMethodCreated(meta: UserMeta, planInfo: PlanInfo): void {
    stanAnalytics('subscription-creation-create-payment-method', { meta });

    trackFacebookEvent('AddPaymentInfo', {
        content_category: 'Signup Process',
        value: planInfo.price,
        currency: planInfo.currency,
    });
}

/**
 * Track subscription creation request
 */
export function trackSubscriptionCreating(meta: UserMeta): void {
    stanAnalytics('subscription-creation-create-subscription', { meta });
}

/**
 * Track subscription creation failure
 */
export function trackSubscriptionFailed(meta: UserMeta, reason: string): void {
    stanAnalytics('subscription-creation-failed', {
        meta,
        props: { reason },
    });
}

/**
 * Track successful subscription
 */
export function trackSubscriptionSuccess(meta: UserMeta, planInfo: PlanInfo): void {
    stanAnalytics('subscription', {
        meta,
        props: {
            email: meta.email,
            fullName: meta.full_name,
        },
    });

    trackFacebookEvent('StartTrial', {
        content_category: 'Signup Process',
        value: planInfo.price,
        currency: planInfo.currency,
        subscription_type: planInfo.isYearly ? 'Yearly' : 'Monthly',
    });

    trackTikTokEvent('StartTrial');

    stanAnalytics('pixel-Purchase', { meta });

    // Google Ads conversions
    if (window.gtag) {
        window.gtag('event', 'conversion', { send_to: GOOGLE_AD_CONV_ACTION.STAN.PURCHASE });
        window.gtag('event', 'conversion', { send_to: GOOGLE_AD_CONV_ACTION.STAN_V2.PURCHASE });
    }
}

/**
 * Fire Northbeam purchase event
 * @param subscriptionId - The Stripe subscription ID
 * @param invoiceId - The Stripe invoice ID (from latest_invoice.id)
 */
export function fireNorthbeamPurchase(
    subscriptionId: string,
    invoiceId: string,
    planInfo: PlanInfo,
    userId?: string | number
): void {
    try {
        window.Northbeam?.firePurchaseEvent({
            id: `${subscriptionId}_${invoiceId}`,
            totalPrice: planInfo.price,
            shippingPrice: 0,
            taxPrice: 0,
            coupons: '',
            currency: planInfo.currency,
            customerId: userId,
            lineItems: [
                {
                    productId: 'Stan Subscription',
                    variantId: planInfo.planName,
                    productName: 'Stan Subscription',
                    variantName: planInfo.planName,
                    price: planInfo.price,
                    quantity: 1,
                },
            ],
        });
    } catch (error) {
        console.error('Northbeam error:', error);
    }
}

/**
 * Fire Northbeam reactivation event for returning users
 */
export function fireNorthbeamReactivation(): void {
    try {
        window.Northbeam?.fireCustomGoal('custom_goal_reactivation', {});
    } catch (error) {
        console.error('Northbeam reactivation error:', error);
    }
}

/**
 * Track when payment method is already used by other accounts
 */
export function trackPaymentMethodHasOtherAccounts(meta: UserMeta, otherEmails: string[]): void {
    stanAnalytics('subscription-creation-payment-method-has-other-accounts', {
        meta: { ...meta, other_emails: otherEmails },
    });
}

/**
 * Track when user chooses to go to login from duplicate payment modal
 */
export function trackGoToLoginFromDuplicatePayment(meta: UserMeta): void {
    stanAnalytics('subscription-creation-go-to-login', { meta });
}
